/**
 * Diagnostic DOSÉ — parcours de questions à étapes qui identifie un type de
 * cheveux, puis en déduit un protocole.
 *
 * Le composant ne connaît pas les questions : il lit ce que l'éditeur a rendu
 * dans le DOM (blocs `_diagnostic-question`, `_diagnostic-option`,
 * `_diagnostic-result`). Une seule source de vérité, pas de configuration
 * dupliquée en JSON.
 *
 * Calcul par famille : une option déclare des profils préfixés,
 * `nature:ondule` ou `etat:fragilise`. Chaque famille élit son gagnant
 * indépendamment. La nature nomme le type affiché, l'état choisit le
 * protocole. Quatre natures et quatre états donnent seize types possibles
 * pour quatre protocoles à rédiger.
 */

const DEFAULT_FAMILY = 'etat';

class DoseDiagnostic extends HTMLElement {
  // Nettoyage automatique des event listeners au démontage
  #controller = new AbortController();

  #questions = [];
  #results = [];
  #originals = new WeakMap();
  #session = '';
  #envoye = false;
  #step = -1; // -1 = écran d'accueil

  connectedCallback() {
    const { signal } = this.#controller;

    this.#questions = Array.from(this.querySelectorAll('[data-diagnostic-question]'));
    this.#results = Array.from(this.querySelectorAll('[data-diagnostic-result]'));

    // Sans question ou sans protocole, le parcours n'a pas de sens :
    // on le signale dans l'éditeur plutôt que d'afficher un écran mort.
    if (this.#questions.length === 0 || this.#results.length === 0) {
      this.#show(this.#ref('empty'));
      this.#hide(this.#ref('intro'));
      return;
    }

    this.#prepareInputs();
    this.#restore();

    this.#ref('start')?.addEventListener('click', () => this.#goTo(0), { signal });
    this.#ref('prev')?.addEventListener('click', () => this.#goTo(this.#step - 1), { signal });
    this.#ref('next')?.addEventListener('click', () => this.#advance(), { signal });
    this.#ref('restart-button')?.addEventListener('click', () => this.#restart(), { signal });

    // Un seul listener délégué pour toutes les options, quel que soit leur nombre
    this.addEventListener('change', this.#handleChange, { signal });
    this.addEventListener('input', this.#handleChange, { signal });

    this.#ref('save-button')?.addEventListener('click', () => this.#openSaveForm(), { signal });

    // Un parcours abandonné se perd sinon : on l'envoie au moment où la page
    // est quittée ou masquée, seul instant où l'on sait que ça n'ira pas plus loin.
    document.addEventListener('visibilitychange', this.#handleLeave, { signal });
    window.addEventListener('pagehide', this.#handleLeave, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /* ---------------------------------------------------------------- Refs */

  #ref(name) {
    return this.querySelector(`[data-ref="${name}"]`);
  }

  #hide(el) {
    if (el) el.hidden = true;
  }

  #show(el) {
    if (el) el.hidden = false;
  }

  /* ------------------------------------------------------------ Préparation */

  /**
   * Pose le type et le `name` des champs d'après le mode de la question
   * parente. Le bloc option ne peut pas les connaître : il est rendu sans
   * savoir dans quelle question il atterrit.
   */
  #prepareInputs() {
    this.#questions.forEach((question, index) => {
      if (question.dataset.mode === 'text') return;

      const single = question.dataset.mode !== 'multiple';
      const name = `${this.getAttribute('storage-key') || 'diagnostic'}--q${index}`;

      question.querySelectorAll('[data-diagnostic-option] input').forEach((input) => {
        input.type = single ? 'radio' : 'checkbox';
        input.name = name;
      });
    });
  }

  /**
   * Découpe « famille:valeur » ; sans préfixe, la valeur rejoint la famille
   * par défaut, ce qui garde les anciennes configurations fonctionnelles.
   */
  #parseProfile(raw) {
    const value = raw.trim().toLowerCase();
    if (!value) return null;

    const [head, tail] = value.split(':');
    return tail ? { family: head.trim(), key: tail.trim() } : { family: DEFAULT_FAMILY, key: head };
  }

  /* -------------------------------------------------------------- Navigation */

  #goTo(step) {
    const clamped = Math.max(0, Math.min(step, this.#questions.length - 1));
    this.#step = clamped;

    this.#hide(this.#ref('intro'));
    this.#hide(this.#ref('restart'));
    this.#show(this.#ref('nav'));
    this.#show(this.#ref('progress'));

    this.#results.forEach((result) => this.#hide(result));
    this.#questions.forEach((question, index) => {
      question.hidden = index !== clamped;
    });

    // La navigation est unique dans la page : on la déplace sous les options
    // de l'étape active, à l'emplacement que le bloc question réserve.
    const slot = this.#questions[clamped].querySelector('[data-nav-slot]');
    const nav = this.#ref('nav');
    if (slot && nav && nav.parentElement !== slot) slot.appendChild(nav);

    this.#applyTokens();
    this.#syncNav();
    this.#updateProgress();
    this.#persist();

    // Le focus suit l'étape, sinon la navigation clavier reste bloquée en haut
    const legend = this.#questions[clamped].querySelector('.diagnostic-question__heading');
    if (legend) {
      legend.setAttribute('tabindex', '-1');
      legend.focus({ preventScroll: true });
    }
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  #advance() {
    if (!this.#isAnswered(this.#step)) return;

    if (this.#step >= this.#questions.length - 1) {
      this.#reveal();
    } else {
      this.#goTo(this.#step + 1);
    }
  }

  #restart() {
    this.#questions.forEach((question) => {
      question.querySelectorAll('input').forEach((input) => {
        input.checked = false;
      });
      const free = question.querySelector('[data-diagnostic-input]');
      if (free) free.value = '';
    });

    try {
      localStorage.removeItem(this.#storageKey);
    } catch {
      // Stockage indisponible (navigation privée) — sans conséquence ici
    }

    this.#step = -1;
    this.#session = '';
    this.#envoye = false;
    this.#results.forEach((result) => this.#hide(result));
    this.#questions.forEach((question) => this.#hide(question));
    this.#hide(this.#ref('nav'));
    this.#hide(this.#ref('progress'));
    this.#hide(this.#ref('restart'));
    this.#hide(this.#ref('save'));
    this.#hide(this.#ref('save-form'));
    this.#show(this.#ref('intro'));
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* ----------------------------------------------------------------- État */

  #handleChange = (event) => {
    const inOption = event.target.closest('[data-diagnostic-option]');
    const isFreeText = event.target.matches('[data-diagnostic-input]');
    if (!inOption && !isFreeText) return;

    this.#syncNav();
    this.#persist();
  };

  #isAnswered(step) {
    const question = this.#questions[step];
    if (!question) return false;

    if (question.dataset.mode === 'text') {
      return (question.querySelector('[data-diagnostic-input]')?.value || '').trim().length > 0;
    }

    const checked = question.querySelectorAll('input:checked').length;
    const min = question.dataset.mode === 'multiple' ? Number(question.dataset.min) || 1 : 1;

    return checked >= min;
  }

  #syncNav() {
    const next = this.#ref('next');
    if (next) next.disabled = !this.#isAnswered(this.#step);

    const prev = this.#ref('prev');
    if (prev) prev.hidden = this.#step <= 0;
  }

  #updateProgress() {
    const bar = this.#ref('progress-bar');
    const label = this.#ref('progress-label');
    const total = this.#questions.length;
    const current = this.#step + 1;

    if (bar) bar.style.width = `${(current / total) * 100}%`;
    if (label) label.textContent = `Étape ${current} sur ${total}`;
  }

  /* ------------------------------------------------------------- Collecte */

  /** Identifiant de session, stable tant que le parcours n'est pas recommencé. */
  get #sessionId() {
    if (!this.#session) {
      this.#session = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    }
    return this.#session;
  }

  #handleLeave = () => {
    if (document.visibilityState === 'visible') return;
    // Uniquement les parcours entamés et non encore transmis.
    if (this.#step < 0 || this.#envoye) return;
    this.#envoyer(false);
  };

  /** Les réponses données, indexées par l'intitulé de la question. */
  #reponsesDonnees() {
    const out = {};

    this.#questions.forEach((question) => {
      const intitule = question
        .querySelector('.diagnostic-question__heading')
        ?.textContent.trim();
      if (!intitule) return;

      if (question.dataset.mode === 'text') {
        const valeur = (question.querySelector('[data-diagnostic-input]')?.value || '').trim();
        if (valeur) out[intitule] = valeur;
        return;
      }

      const labels = Array.from(question.querySelectorAll('input:checked'))
        .map((input) =>
          input.closest('[data-diagnostic-option]')?.querySelector('.diagnostic-option__label')
            ?.textContent.trim()
        )
        .filter(Boolean);

      if (labels.length) out[intitule] = labels.join(', ');
    });

    return out;
  }

  /**
   * Transmet le parcours une seule fois. `sendBeacon` est utilisé pour les deux
   * cas : c'est le seul envoi que le navigateur garantit même lorsque la page
   * se ferme, et le type texte évite toute requête préparatoire.
   */
  #envoyer(termine, gagnant = null) {
    const url = this.getAttribute('collect-url');
    if (!url || this.#envoye) return;

    const tokens = this.#collectTokens();
    const lire = (selecteur) => gagnant?.querySelector(selecteur)?.textContent.trim() || '';
    const protocole = gagnant
      ? Array.from(gagnant.querySelectorAll('.diagnostic-result__protocol-item')).map((item) =>
          item.querySelector('dd')?.textContent.trim()
        )
      : [];

    const charge = {
      session: this.#sessionId,
      termine,
      etape: this.#step + 1,
      prenom: tokens.prenom || '',
      type: lire('[data-ref="type-line"]'),
      problemes: gagnant
        ? Array.from(gagnant.querySelectorAll('.diagnostic-result__concern-label')).map((n) =>
            n.textContent.trim()
          )
        : [],
      protocole: gagnant?.dataset.typeLabel || '',
      dose: protocole[0] || '',
      rythme: protocole[1] || '',
      duree: protocole[2] || '',
      quantite: protocole[3] || '',
      produits: tokens.produits || '',
      frequence: '',
      reponses: this.#reponsesDonnees(),
    };

    // La fréquence d'usage est la dernière question : on la relit par son nom.
    const freq = Object.entries(charge.reponses).find(([q]) => /appliquez-vous/i.test(q));
    if (freq) charge.frequence = freq[1];

    // La feuille attend une clé « Âge » dédiée, alors que les réponses sont
    // indexées par l'intitulé complet des questions.
    if (tokens.age) charge.reponses['Âge'] = tokens.age;

    try {
      const blob = new Blob([JSON.stringify(charge)], { type: 'text/plain;charset=utf-8' });
      if (navigator.sendBeacon(url, blob)) this.#envoye = true;
    } catch {
      // Une collecte qui échoue ne doit jamais se voir côté cliente.
    }
  }

  /* ---------------------------------------------------------------- Jetons */

  /**
   * Les réponses nommées deviennent des jetons réutilisables : une question
   * portant `data-token="prenom"` permet d'écrire {{prenom}} dans n'importe
   * quelle question suivante ou dans un protocole.
   */
  #collectTokens() {
    const tokens = {};

    this.#questions.forEach((question) => {
      const name = (question.dataset.token || '').trim();
      if (!name) return;

      if (question.dataset.mode === 'text') {
        const value = (question.querySelector('[data-diagnostic-input]')?.value || '').trim();
        if (value) tokens[name] = value;
        return;
      }

      const labels = Array.from(question.querySelectorAll('input:checked'))
        .map((input) =>
          input.closest('[data-diagnostic-option]')?.querySelector('.diagnostic-option__label')
            ?.textContent.trim()
        )
        .filter(Boolean);

      if (labels.length) tokens[name] = this.#joinFr(labels);
    });

    return tokens;
  }

  /**
   * « a, b et c » plutôt que « a, b, c » : on écrit du français, pas une liste.
   * Seul le premier terme garde sa majuscule, les suivants sont en cours de
   * phrase.
   */
  #joinFr(items) {
    if (!items.length) return '';

    const parts = items.map((item, index) =>
      index === 0 ? item : item.charAt(0).toLowerCase() + item.slice(1)
    );
    if (parts.length < 2) return parts[0];

    return `${parts.slice(0, -1).join(', ')} et ${parts[parts.length - 1]}`;
  }

  /**
   * Applique les jetons sur tous les textes qui en contiennent. Le texte
   * d'origine est mémorisé la première fois, sinon un jeton remplacé ne
   * pourrait plus l'être une seconde fois avec une autre réponse.
   */
  #applyTokens() {
    const tokens = this.#collectTokens();

    this.querySelectorAll('[data-diagnostic-question], [data-diagnostic-result]').forEach((scope) => {
      scope
        .querySelectorAll(
          '.diagnostic-question__heading, .diagnostic-question__subheading, .diagnostic-result__eyebrow, .diagnostic-result__title, .diagnostic-result__state p'
        )
        .forEach((node) => {
          if (!this.#originals.has(node)) this.#originals.set(node, node.textContent);
          const source = this.#originals.get(node);
          if (!source.includes('[[')) return;

          node.textContent = source.replace(/\[\[\s*([a-z0-9_-]+)\s*\]\]/gi, (match, key) =>
            Object.prototype.hasOwnProperty.call(tokens, key) ? tokens[key] : ''
          );
        });
    });

    return tokens;
  }

  /* -------------------------------------------------------------- Résultat */

  /**
   * Additionne les poids par famille et par clé. Retourne, pour chaque
   * famille, la clé gagnante et le libellé de l'option qui a le plus pesé :
   * ce libellé sert à nommer le type affiché.
   */
  #score() {
    const families = new Map();

    this.#questions.forEach((question) => {
      question.querySelectorAll('input:checked').forEach((input) => {
        const option = input.closest('[data-diagnostic-option]');
        if (!option) return;

        const weight = Number(option.dataset.weight) || 1;
        const label = option.querySelector('.diagnostic-option__label')?.textContent.trim() || '';

        (option.dataset.profiles || '')
          .split(',')
          .map((raw) => this.#parseProfile(raw))
          .filter(Boolean)
          .forEach(({ family, key }) => {
            if (!families.has(family)) families.set(family, new Map());
            const scores = families.get(family);
            const previous = scores.get(key) || { score: 0, label: '', best: 0 };

            scores.set(key, {
              score: previous.score + weight,
              // On garde le libellé de l'option la plus lourde pour cette clé
              label: weight >= previous.best ? label : previous.label,
              best: Math.max(previous.best, weight),
            });
          });
      });
    });

    const winners = {};
    families.forEach((scores, family) => {
      let key = null;
      let label = '';
      let best = 0;

      scores.forEach((entry, candidate) => {
        if (entry.score > best) {
          best = entry.score;
          key = candidate;
          label = entry.label;
        }
      });

      // `scores` est conservé brut : les protocoles par combinaison en ont
      // besoin pour vérifier plusieurs états à la fois, pas seulement le
      // premier du classement.
      const raw = new Map();
      scores.forEach((entry, candidate) => raw.set(candidate, entry.score));

      winners[family] = { key, label, score: best, scores: raw };
    });

    return winners;
  }

  /**
   * Révèle le protocole correspondant à l'état gagnant. À égalité, le premier
   * protocole dans l'ordre de l'éditeur l'emporte ; si aucun état ne ressort,
   * on retombe sur le dernier, qui doit donc être le plus général.
   */
  #reveal() {
    const winners = this.#score();
    const etat = winners[DEFAULT_FAMILY]?.key || null;
    const etatScores = winners[DEFAULT_FAMILY]?.scores || new Map();

    // Un protocole peut se déclarer par combinaison plutôt que par tête de
    // classement : « fragilisé ET terne » l'emporte sur « fragilisé » seul,
    // parce qu'un cheveu qui cumule les deux atteintes ne se traite pas comme
    // un cheveu qui n'en a qu'une. Ces règles priment sur le score simple.
    const combined = this.#results.filter((result) => {
      const keys = (result.dataset.combine || '')
        .split(',')
        .map((key) => key.trim().toLowerCase())
        .filter(Boolean);
      if (keys.length < 2) return false;

      const threshold = Number(result.dataset.combineThreshold) || 1;
      return keys.every((key) => (etatScores.get(key) || 0) >= threshold);
    });

    let winner;
    if (combined.length) {
      // Gravité d'une combinaison : le plus faible des deux axes, car c'est lui
      // qui limite ce que la personne a réellement à traiter des deux côtés.
      const severity = Math.min(
        ...(combined[0].dataset.combine || '')
          .split(',')
          .map((key) => etatScores.get(key.trim().toLowerCase()) || 0)
      );
      winner = this.#pickBySeverity(combined, severity);
    }

    if (!winner) {
      const candidates = this.#results.filter(
        (result) => (result.dataset.profile || '').trim().toLowerCase() === etat
      );
      winner = this.#pickBySeverity(candidates, etatScores.get(etat) || 0);
    }

    if (!winner) winner = this.#results[this.#results.length - 1];

    // Type composé : « Bouclé · Fragilisé ». La nature vient de l'option
    // choisie, l'état du protocole lui-même.
    const natureLabel = winners.nature?.label || '';
    const etatLabel = winner.dataset.typeLabel || '';
    const typeLine = winner.querySelector('[data-ref="type-line"]');
    if (typeLine) {
      typeLine.textContent = [natureLabel, etatLabel].filter(Boolean).join(' · ');
    }

    this.#fillNature(winner, winners.nature?.key);
    this.#fillNotes(winner);
    this.#fillDate(winner);
    this.#fillConcerns(winner);

    this.#questions.forEach((question) => this.#hide(question));
    this.#results.forEach((result) => this.#hide(result));
    this.#hide(this.#ref('nav'));
    this.#hide(this.#ref('progress'));
    this.#show(winner);
    this.#show(this.#ref('save'));
    this.#show(this.#ref('restart'));

    // Les jetons ne sont appliqués qu'ici pour le protocole : le prénom n'est
    // connu qu'une fois la première question répondue.
    this.#applyTokens();
    this.#fillSaveNote(winner);
    this.#envoyer(true, winner);

    this.#step = this.#questions.length; // hors parcours = résultat affiché
    this.#persist();

    winner.setAttribute('tabindex', '-1');
    winner.focus({ preventScroll: true });
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // Permet de brancher un suivi analytique sans toucher à ce fichier
    this.dispatchEvent(
      new CustomEvent('diagnostic:complete', {
        bubbles: true,
        detail: { etat, nature: winners.nature?.key || null, winners },
      })
    );
  }

  /**
   * Parmi des protocoles qui partagent le même état, retient celui dont le
   * score minimum est le plus élevé sans dépasser le score obtenu. C'est ce qui
   * permet de graduer : le même axe donne une cure d'un flacon si l'atteinte
   * est légère, de trois si elle est sévère.
   */
  #pickBySeverity(candidates, score) {
    return candidates
      .filter((result) => (Number(result.dataset.scoreMin) || 0) <= score)
      .sort(
        (a, b) => (Number(b.dataset.scoreMin) || 0) - (Number(a.dataset.scoreMin) || 0)
      )[0];
  }

  /**
   * Recopie dans le protocole le texte de la nature gagnante. Les natures sont
   * rendues dans des `<template>` : quatre textes de nature croisés avec quatre
   * protocoles produisent seize comptes rendus distincts.
   */
  #fillNature(winner, natureKey) {
    const slot = winner.querySelector('[data-ref="nature-text"]');
    if (!slot) return;

    slot.replaceChildren();
    if (!natureKey) return;

    const source = this.querySelector(
      `[data-diagnostic-nature][data-nature="${CSS.escape(natureKey)}"]`
    );
    if (source) slot.append(source.content.cloneNode(true));
  }

  /**
   * Liste les constats attachés aux réponses réellement cochées, dans l'ordre
   * des questions. C'est ce qui fait la différence entre un compte rendu et un
   * texte générique : chaque ligne vient d'une réponse donnée par la personne.
   */
  #fillNotes(winner) {
    const section = winner.querySelector('[data-ref="notes"]');
    const list = winner.querySelector('[data-ref="notes-list"]');
    if (!section || !list) return;

    const notes = [];
    this.#questions.forEach((question) => {
      question.querySelectorAll('input:checked').forEach((input) => {
        const note = input.closest('[data-diagnostic-option]')?.dataset.note?.trim();
        if (note && !notes.includes(note)) notes.push(note);
      });
    });

    list.replaceChildren(
      ...notes.map((note) => {
        const item = document.createElement('li');
        item.textContent = note;
        return item;
      })
    );

    section.hidden = notes.length === 0;
  }

  /** Un compte rendu se date : c'est ce qui le distingue d'une page marketing. */
  #fillDate(winner) {
    const node = winner.querySelector('[data-ref="date"]');
    if (!node) return;

    node.textContent = new Date().toLocaleDateString('fr-CH', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  /**
   * Les trois problèmes les mieux notés, tirés des réponses cochées. Deux
   * personnes du même type n'auront pas les mêmes encadrés, puisqu'ils
   * dépendent de ce qu'elles ont réellement déclaré.
   */
  #fillConcerns(winner) {
    const list = winner.querySelector('[data-ref="concerns"]');
    if (!list) return;

    const scores = new Map();
    this.#questions.forEach((question) => {
      question.querySelectorAll('input:checked').forEach((input) => {
        const option = input.closest('[data-diagnostic-option]');
        if (!option) return;

        const weight = Number(option.dataset.weight) || 1;
        (option.dataset.concerns || '')
          .split(',')
          .map((label) => label.trim())
          .filter(Boolean)
          .forEach((label) => scores.set(label, (scores.get(label) || 0) + weight));
      });
    });

    const top = [...scores.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3);
    const RANKS = ['Problème principal', 'Problème secondaire', 'Problème complémentaire'];

    list.replaceChildren(
      ...top.map(([label], index) => {
        const item = document.createElement('li');
        const rank = document.createElement('span');
        rank.className = 'diagnostic-result__concern-rank';
        rank.textContent = RANKS[index] || '';
        const name = document.createElement('span');
        name.className = 'diagnostic-result__concern-label';
        name.textContent = label;
        item.append(rank, name);
        return item;
      })
    );

    list.hidden = top.length === 0;
  }

  /** Ouvre le formulaire d'enregistrement et place le focus dans le champ. */
  #openSaveForm() {
    const form = this.#ref('save-form');
    if (!form) return;

    form.hidden = false;
    this.#hide(this.#ref('save-button'));
    form.querySelector('input[type="email"]')?.focus();
  }

  /**
   * Glisse le résultat dans la note du contact : sans cela, l'email arrive
   * sans le diagnostic auquel il correspond.
   */
  #fillSaveNote(winner) {
    const note = this.#ref('save-note');
    if (!note) return;

    const type = winner.querySelector('[data-ref="type-line"]')?.textContent.trim() || '';
    const protocole = Array.from(winner.querySelectorAll('.diagnostic-result__protocol-item'))
      .map((item) => `${item.querySelector('dt')?.textContent}: ${item.querySelector('dd')?.textContent.trim()}`)
      .join(' | ');

    note.value = [`Type: ${type}`, protocole].filter(Boolean).join(' — ');
  }

  /* ------------------------------------------------------------ Persistance */

  get #storageKey() {
    return this.getAttribute('storage-key') || 'dose-diagnostic';
  }

  #persist() {
    if (!this.hasAttribute('resume')) return;

    const answers = this.#questions.map((question) =>
      question.dataset.mode === 'text'
        ? [question.querySelector('[data-diagnostic-input]')?.value || '']
        : Array.from(question.querySelectorAll('input:checked')).map((input) => input.value)
    );

    try {
      localStorage.setItem(this.#storageKey, JSON.stringify({ step: this.#step, answers }));
    } catch {
      // Quota atteint ou stockage bloqué — le parcours reste utilisable
    }
  }

  #restore() {
    if (!this.hasAttribute('resume')) return;

    let saved = null;
    try {
      saved = JSON.parse(localStorage.getItem(this.#storageKey) || 'null');
    } catch {
      return;
    }

    if (!saved || !Array.isArray(saved.answers)) return;

    saved.answers.forEach((values, index) => {
      const question = this.#questions[index];
      if (!question) return;

      if (question.dataset.mode === 'text') {
        const free = question.querySelector('[data-diagnostic-input]');
        if (free && values[0]) free.value = values[0];
        return;
      }

      values.forEach((value) => {
        const input = question.querySelector(`input[value="${CSS.escape(value)}"]`);
        if (input) input.checked = true;
      });
    });

    // On ne saute pas directement à l'étape mémorisée : l'écran d'accueil
    // reste la porte d'entrée, mais les réponses sont déjà là.
  }
}

if (!customElements.get('dose-diagnostic')) {
  customElements.define('dose-diagnostic', DoseDiagnostic);
}
